import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { comMarcador } from '@/lib/campanha';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

type EventType = 
  | 'page_view' 
  | 'whatsapp_click' 
  | 'map_click' 
  | 'job_application_submit'
  | 'cta_click'
  | 'link_click'
  // Funil da landing page de evento (/wellness). Vao para o dataLayer com o
  // mesmo nome, para o GTM poder mapea-los sem traducao.
  | 'wellness_page_view'
  | 'wellness_form_start'
  | 'wellness_form_submit'
  | 'wellness_lead_created'
  | 'wellness_instagram_click'
  | 'wellness_program_details_click'
  | 'wellness_final_cta_click';

interface EventData {
  [key: string]: string | number | boolean | undefined;
}

export const useTrackEvent = () => {
  const trackEvent = useCallback(async (eventType: EventType, eventData?: EventData) => {
    // 1. Enviar para GTM
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: eventType,
        ...eventData,
      });
    }

    // 2. Enviar para Supabase
    try {
      await supabase.from('site_events').insert({
        event_type: eventType,
        event_data: eventData || {},
        page_url: typeof window !== 'undefined' ? window.location.pathname : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
    } catch (error) {
      // Silently fail - don't break UX for analytics
      console.error('Error tracking event:', error);
    }
  }, []);

  // Função para tracking com garantia de envio antes de navegação externa
  const trackEventBeforeNavigation = useCallback((
    eventType: EventType, 
    eventData?: EventData,
    navigateTo?: string,
    delay = 150
  ) => {
    // 1. Enviar para GTM imediatamente
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: eventType,
        ...eventData,
      });
    }

    let hasNavigated = false;

    // O destino é resolvido aqui, e não no href do <a>: estes botões chamam
    // preventDefault e navegam por window.open, então precisam pedir a marca
    // de campanha explicitamente (comMarcador devolve URL não-WhatsApp intacta).
    const destino = navigateTo ? comMarcador(navigateTo) : undefined;

    const navigate = () => {
      if (!hasNavigated && destino) {
        hasNavigated = true;
        window.open(destino, '_blank');
      }
    };

    // 2. Enviar para Supabase
    supabase.from('site_events').insert({
      event_type: eventType,
      event_data: eventData || {},
      page_url: typeof window !== 'undefined' ? window.location.pathname : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    }).then(navigate);

    // Fallback: garantir navegação mesmo se a promise demorar
    setTimeout(navigate, delay);
  }, []);

  return { trackEvent, trackEventBeforeNavigation };
};

// Hook para rastrear page views automaticamente
export const usePageView = (pageName?: string) => {
  const { trackEvent } = useTrackEvent();
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!hasTracked.current) {
      hasTracked.current = true;
      trackEvent('page_view', { page_name: pageName });
    }
  }, [trackEvent, pageName]);
};
